
import {
  createRouter,
  createWebHistory,
} from "vue-router";

const routes = [
  {
    name: "AppLayout",

    path: "/app-layout",

    redirect: {
      name: "BookedAppointment",
    },

    component: () =>
      import("@/layouts/AppLayout.vue"),

    meta: {
      requiresAuth: true,
    },

    children: [
      // =====================================================
      // BOOKED APPOINTMENTS
      // =====================================================
      {
        name: "BookedAppointment",

        path: "/booked-appointments",

        component: () =>
          import(
            "@/views/BookedAppointment.vue"
          ),

        meta: {
          isShowTitleInAppBar: true,
          title: "Booked Appointments",
        },
      },

      // =====================================================
      // VIN SCAN
      // =====================================================
      {
        name: "VinScan",

        path: "/vin-scan",

        component: () =>
          import("@/views/VinScan.vue"),

        meta: {
          isShowTitleInAppBar: false,
          title: "VIN Scan",
        },
      },

      // =====================================================
      // CUSTOMER VEHICLE DETAIL
      // =====================================================
      {
        name: "CustomerVehicleDetail",

        path: "/customer-vehicle-info",

        component: () =>
          import(
            "@/views/CustAndVehicleInfo.vue"
          ),

        meta: {
          isShowTitleInAppBar: true,
          title: "Customer Vehicle Info",
        },
      },

      // =====================================================
      // NEW CHECK-IN WORKSPACE
      //
      // SINGLE APP BAR + 5 STEPS:
      //
      // 1. Verify
      // 2. Odometer
      // 3. Video Walkaround
      // 4. Photos
      // 5. Packages
      //
      // All steps render inside CheckInWorkspace.vue.
      // No separate page navigation between steps.
      // =====================================================
      {
        name: "CheckInWorkspace",

        path: "/check-in",

        component: () =>
          import(
            "@/views/CheckInWorkspace.vue"
          ),

        meta: {
          requiresAuth: true,

          isShowTitleInAppBar: false,

          title: "Vehicle Check-In",

          // Hide left AppLayout navigation drawer.
          hideDrawer: true,

          // Custom CheckInProgressHeader handles the app bar.
          hideAppBar: true,

          // Additional metadata for check-in screens.
          isCheckInWorkspace: true,
        },
      },

      // =====================================================
      // EXISTING VIDEO WALKAROUND
      //
      // Kept for compatibility with older navigation.
      // New check-in flow uses CheckInWorkspace instead.
      // =====================================================
      {
        name: "VideoWalkaround",

        path: "/video-walkaround",

        component: () =>
          import(
            "@/views/VideoWalkaround.vue"
          ),

        meta: {
          isShowTitleInAppBar: false,
          title: "Video Walkaround",
          hideDrawer: true,
        },
      },

      // =====================================================
      // UPLOADED WALKAROUND FILES
      // =====================================================
      {
        name: "UploadWalkaroundFiles",

        path: "/upload-walkaround-files",

        component: () =>
          import(
            "@/views/UploadWalkaroundFiles.vue"
          ),

        meta: {
          isShowTitleInAppBar: true,
          title: "Uploaded Files",
        },
      },

      // =====================================================
      // EXISTING OIL CHANGE PACKAGES
      //
      // Kept for compatibility.
      // New flow renders PackagesStep inside the workspace.
      // =====================================================
      {
        name: "OilChangePackages",

        path: "/oil-packages",

        component: () =>
          import(
            "@/views/OilChangePackages.vue"
          ),

        meta: {
          isShowTitleInAppBar: false,
          title: "Oil Change Packages",
        },
      },

      // =====================================================
      // SERVICES
      // =====================================================
      {
        name: "services",

        path: "/services",

        component: () =>
          import("@/views/Services.vue"),

        meta: {
          isShowTitleInAppBar: true,
          title: "Services",

          // Hide left navigation bar on Services page.
          hideDrawer: true,
        },
      },

      // =====================================================
      // WALKAROUND DETAILS
      // =====================================================
      {
        name: "WalkaroundView",

        path: "/walkaround/:walkaround",

        component: () =>
          import(
            "@/views/WalkaroundView.vue"
          ),

        meta: {
          isShowTitleInAppBar: true,
          title: "Walkaround Details",
        },
      },

      // =====================================================
      // COMPLETED APPOINTMENTS
      // =====================================================
      {
        name: "CompletedAppointments",

        path: "/completed-appointments",

        component: () =>
          import(
            "@/views/CompletedAppointments.vue"
          ),

        meta: {
          isShowTitleInAppBar: true,
          title: "Completed Appointments",
        },
      },

      // =====================================================
      // REPORTS
      // =====================================================
      {
        name: "Reports",

        path: "/reports",

        component: () =>
          import("@/views/Reports.vue"),

        meta: {
          isShowTitleInAppBar: true,
          title: "Reports",
        },
      },

      // =====================================================
      // SETTINGS
      // =====================================================
      {
        name: "Settings",

        path: "/settings",

        component: () =>
          import("@/views/Settings.vue"),

        meta: {
          isShowTitleInAppBar: true,
          title: "Settings",
        },
      },
    ],
  },

  // =====================================================
  // FORGOT PASSWORD
  // =====================================================
  {
    name: "forgot-password",

    path: "/forgot-password",

    component: () =>
      import(
        "@/views/ForgotPassword.vue"
      ),

    meta: {
      requiresAuth: false,
      isShowTitleInAppBar: true,
      title: "forgot-password",
    },
  },

  // =====================================================
  // RESET PASSWORD
  // =====================================================
  {
    name: "reset-password",

    path: "/reset-password",

    component: () =>
      import(
        "@/views/ResetPassword.vue"
      ),

    meta: {
      requiresAuth: false,
      isShowTitleInAppBar: true,
      title: "reset-password",
    },
  },

  // =====================================================
  // QR LOGIN
  // =====================================================
  {
    name: "qr-login",

    path: "/qr-login",

    component: () =>
      import("@/views/qr-login.vue"),

    meta: {
      requiresAuth: false,
      isShowTitleInAppBar: true,
      title: "qr-login",
    },
  },

  // =====================================================
  // LOGIN
  // =====================================================
  {
    name: "Login",

    path: "/",

    component: () =>
      import("@/views/Login.vue"),

    meta: {
      requiresAuth: false,
      title: "Login",
    },

    beforeEnter: (to, from, next) => {
      const token = JSON.parse(
        localStorage.getItem("token")
      );

      if (token) {
        next({
          name: "BookedAppointment",
        });
      } else {
        next();
      }
    },
  },

  // =====================================================
  // PAGE NOT FOUND
  // =====================================================
  {
    name: "PageNotFound",

    path: "/:pathMatch(.*)*",

    component: () =>
      import(
        "@/views/PageNotFound.vue"
      ),

    meta: {
      requiresAuth: false,
      title: "PageNotFound",
    },
  },
];

// =====================================================
// CREATE ROUTER
// =====================================================
const router = createRouter({
  history: createWebHistory(
    process.env.BASE_URL
  ),

  routes,
});

// =====================================================
// AUTHENTICATION GUARD
// =====================================================
router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(
    (record) => record.meta.requiresAuth
  );

  const token = JSON.parse(
    localStorage.getItem("token")
  );

  if (requiresAuth && !token) {
    next({
      name: "Login",
    });

    return;
  }

  next();
});

export default router;
